import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../services/api/axios";

const BASE_URL = "/v1/admin/personal/product-updates";

// list campaigns
const fetchProductUpdates = async () => {
  const response = await api.get(BASE_URL);
  return response.data;
};

export const useProductUpdates = () => {
  return useQuery({
    queryKey: ["product-updates"],
    queryFn: fetchProductUpdates,
    refetchOnWindowFocus: false,
  });
};

// single campaign
const fetchProductUpdate = async (uuid) => {
  const { data } = await api.get(`${BASE_URL}/${uuid}`);
  return data;
};

export const useProductUpdate = (uuid) => {
  return useQuery({
    queryKey: ["product-update", uuid],
    queryFn: () => fetchProductUpdate(uuid),
    enabled: !!uuid,
    refetchOnWindowFocus: false,
  });
};

// create campaign
const createProductUpdate = async (payload) => {
  const { data } = await api.post(BASE_URL, payload);
  return data;
};

export const useCreateProductUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createProductUpdate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-updates"] });
    },
  });
};

// update campaign (draft only)
const updateProductUpdate = async (uuid, payload) => {
  const { data } = await api.patch(`${BASE_URL}/${uuid}`, payload);
  return data;
};

export const useUpdateProductUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, payload }) => updateProductUpdate(uuid, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["product-updates"] });
      queryClient.invalidateQueries({ queryKey: ["product-update", variables.uuid] });
    },
  });
};

// delete campaign
const deleteProductUpdate = async (uuid) => {
  const { data } = await api.delete(`${BASE_URL}/${uuid}`);
  return data;
};

export const useDeleteProductUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteProductUpdate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-updates"] });
    },
  });
};

// send test
const sendTestProductUpdate = async (uuid, testEmail) => {
  const { data } = await api.post(`${BASE_URL}/${uuid}/send-test`, { testEmail });
  return data;
};

export const useSendTestProductUpdate = () => {
  return useMutation({
    mutationFn: ({ uuid, testEmail }) => sendTestProductUpdate(uuid, testEmail),
  });
};

// send (trigger the real bulk send)
const sendProductUpdate = async (uuid) => {
  const { data } = await api.post(`${BASE_URL}/${uuid}/send`);
  return data;
};

export const useSendProductUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendProductUpdate,
    onSuccess: (_, uuid) => {
      queryClient.invalidateQueries({ queryKey: ["product-updates"] });
      queryClient.invalidateQueries({ queryKey: ["product-update-progress", uuid] });
    },
  });
};

// pause / resume
const pauseProductUpdate = async (uuid) => {
  const { data } = await api.post(`${BASE_URL}/${uuid}/pause`);
  return data;
};

export const usePauseProductUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pauseProductUpdate,
    onSuccess: (_, uuid) => {
      queryClient.invalidateQueries({ queryKey: ["product-updates"] });
      queryClient.invalidateQueries({ queryKey: ["product-update-progress", uuid] });
    },
  });
};

const resumeProductUpdate = async (uuid) => {
  const { data } = await api.post(`${BASE_URL}/${uuid}/resume`);
  return data;
};

export const useResumeProductUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resumeProductUpdate,
    onSuccess: (_, uuid) => {
      queryClient.invalidateQueries({ queryKey: ["product-updates"] });
      queryClient.invalidateQueries({ queryKey: ["product-update-progress", uuid] });
    },
  });
};

// progress / report - polls while sending
const fetchProgress = async (uuid) => {
  const { data } = await api.get(`${BASE_URL}/${uuid}/progress`);
  return data;
};

export const useProductUpdateProgress = (uuid, { pollWhileSending = false } = {}) => {
  return useQuery({
    queryKey: ["product-update-progress", uuid],
    queryFn: () => fetchProgress(uuid),
    enabled: !!uuid,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      if (!pollWhileSending) return false;
      const status = query.state.data?.data?.jobRun?.status;
      return status === "RUNNING" ? 3000 : false;
    },
  });
};

// image upload for the WYSIWYG composer
const uploadProductUpdateImage = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post(`${BASE_URL}/upload-image`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const useUploadProductUpdateImage = () => {
  return useMutation({ mutationFn: uploadProductUpdateImage });
};

// schedule / unschedule
const scheduleProductUpdate = async (uuid, scheduledAt) => {
  const { data } = await api.post(`${BASE_URL}/${uuid}/schedule`, { scheduledAt });
  return data;
};

export const useScheduleProductUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, scheduledAt }) => scheduleProductUpdate(uuid, scheduledAt),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["product-updates"] });
      queryClient.invalidateQueries({ queryKey: ["product-update", variables.uuid] });
    },
  });
};

const unscheduleProductUpdate = async (uuid) => {
  const { data } = await api.post(`${BASE_URL}/${uuid}/unschedule`);
  return data;
};

export const useUnscheduleProductUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: unscheduleProductUpdate,
    onSuccess: (_, uuid) => {
      queryClient.invalidateQueries({ queryKey: ["product-updates"] });
      queryClient.invalidateQueries({ queryKey: ["product-update", uuid] });
    },
  });
};
